/**
 * debugPresets — named debug-lobby presets: one click (or one URL param)
 * swaps the embedded hub set to a task-scoped kit (DJ 2026-07-11:
 * "debug lobby presets for sound or abilities or etc we can easily switch
 * between and configure").
 *
 * Consumed by:
 *   - DebugSuiteContainer (Shift+D hub) — preset chips in the toolbar;
 *     clicking one applies it atomically to hubWidgets (everything else off,
 *     this set on) without opening floating cards.
 *   - WindowManagerContext.seedFromUrl — `?dbgPreset=<id>` seeds the hub set
 *     on /debug and the floating set on gameplay routes where no hub renders.
 *
 * Configuring: edit this table. A preset is just window ids — every id must
 * exist in WINDOW_REGISTRY (locked by debugPresets.test.ts). Adding a preset
 * is one entry here; the chips + URL seeding pick it up automatically.
 */

import type { WindowId } from '../../game/ui/framework/types';

export interface DebugHubPreset {
  /** URL value: ?dbgPreset=<id> */
  id: string;
  /** Chip text (emoji + short name). */
  label: string;
  /** Tooltip / description. */
  title: string;
  /** The windows this preset enables (everything else turns off). */
  windows: readonly WindowId[];
}

export const DEBUG_HUB_PRESETS: readonly DebugHubPreset[] = [
  {
    id: 'sound',
    label: '🔊 Sound',
    title: 'Sound testing: the Sound Debug card (fire-trace truth, test fires, engine health, Audio Lab bench).',
    windows: ['gmSoundDebug'],
  },
  {
    id: 'abilities',
    label: '✨ Abilities',
    title: 'Ability iteration: gate readout, cast/visual/target lifecycles, test rig, grounds tools.',
    windows: ['targetAbilityCheck', 'abilityLifecycle', 'visualLifecycle', 'targetLifecycle', 'abilityTestRig'],
  },
  {
    id: 'netcode',
    label: '📡 Netcode',
    title: 'Netcode triage: latency, traffic, spikes, position drift, MP diagnostics.',
    windows: ['latency', 'netTraffic', 'netSpike', 'posDrift', 'mpDiagnostics'],
  },
  {
    id: 'perf',
    label: '🎞 Perf',
    title: 'Frame & perf: jitter, smoothness dashboard, perf graph, system health.',
    windows: ['frameJitter', 'smoothnessDashboard', 'perfGraph', 'systemHealth'],
  },
  {
    id: 'race',
    label: '🏁 Race',
    title: 'Race debugging: run perf, position drift, latency.',
    windows: ['raceRunPerf', 'posDrift', 'latency'],
  },
];

/** Preset lookup by id (undefined for unknown ids — callers no-op). */
export function findDebugPreset(id: string | null | undefined): DebugHubPreset | undefined {
  return DEBUG_HUB_PRESETS.find((p) => p.id === id);
}
